import { headingText } from './headingText';
import React, { useEffect } from 'react';
import { doc, updateDoc, increment } from 'firebase/firestore';
import ContentImage from './ContentImage';
import { markStoryAsRead } from './storyReadState';

const SingleStoryPage = ({ story, onBack, db, appId }) => {
    // Scroll to top when component mounts
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        if (story?.id) {
            markStoryAsRead(story.id);
        }
    }, [story]);

    // Increment view count when story is opened
    useEffect(() => {
        const incrementViewCount = async () => {
            if (!db || !story || !story.id) return;
            
            try {
                const storiesCollectionPath = appId ? `/artifacts/${appId}/public/data/stories` : 'stories';
                const storyRef = doc(db, storiesCollectionPath, story.id);
                
                await updateDoc(storyRef, {
                    viewCount: increment(1)
                });
                
                console.log('View count incremented for story:', story.id);
            } catch (error) {
                console.error('Error incrementing view count:', error);
            }
        };

        incrementViewCount();
    }, [db, story, appId]);

    return (
        <div className="bg-gray-900 text-white min-h-screen">
            {/* Dekorativní pozadí */}
            <div className="fixed inset-0 opacity-5 pointer-events-none">
                <div 
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ 
                        backgroundImage: `url('https://images.unsplash.com/photo-1523712999610-f77fbcfc3843?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')` 
                    }}
                ></div>
            </div>

            <div className="relative pt-24 pb-12">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="mb-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <button 
                            onClick={onBack} 
                            className="inline-flex items-center gap-2 rounded-full border border-yellow-400/20 bg-black/30 px-4 py-2.5 text-sm font-semibold text-yellow-300 shadow-lg backdrop-blur transition-all duration-300 hover:border-yellow-300/50 hover:bg-black/45 hover:text-yellow-200 hover:shadow-xl group"
                        >
                            <i aria-hidden="true" className="fas fa-arrow-left transform transition-transform duration-300 group-hover:-translate-x-1"></i> 
                            Zpět do zahrady
                        </button>

                        <div className="text-sm uppercase tracking-[0.22em] text-gray-500 sm:text-right">
                            Zahrada příběhů
                        </div>
                    </div>

                    <div className="mb-6 flex justify-center">
                        <span className="inline-flex items-center gap-2 rounded-full border border-green-400/20 bg-green-400/10 px-3 py-1 text-xs font-semibold tracking-wide text-green-300 backdrop-blur-sm">
                            <span className="h-2 w-2 rounded-full bg-green-300"></span>
                            Přečteno v tomto zařízení
                        </span>
                    </div>
                    
                    {/* Emoji nálady */}
                    {story.mood && (
                        <div className="text-center mb-8">
                            <span className="text-6xl inline-block animate-float">{story.mood}</span>
                        </div>
                    )}
                    
                    {/* Nadpis příběhu */}
                    <h1 className="article-title text-center">
                        {headingText(story.title)}
                    </h1>
                    
                    {/* Autor a meta informace */}
                    <div className="text-center mb-12 space-y-2">
                        {story.author && (
                            <p className="text-lg text-gray-400 italic">— {story.author}</p>
                        )}
                        {story.date && (
                            <p className="text-sm text-gray-500">{story.date}</p>
                        )}
                        {story.category && (
                            <span className="inline-block bg-yellow-600/20 text-yellow-400 px-4 py-1 rounded-full text-sm mt-3">
                                {story.category}
                            </span>
                        )}
                    </div>
                    
                    <div className="w-32 h-1 bg-gradient-to-r from-transparent via-yellow-500 to-transparent mx-auto mb-12"></div>
                    
                    {/* Obrázek příběhu */}
                    {story.image && (
                        <div className="mb-12 rounded-2xl overflow-hidden shadow-2xl">
                            <ContentImage 
                                src={story.image} 
                                alt={story.title}
                                className="w-full h-auto object-cover"
                            />
                        </div>
                    )}
                    
                    {/* Obsah příběhu */}
                    <div className="glass-effect rounded-2xl p-8 md:p-12 mb-12">
                        <div 
                            className="prose prose-lg prose-invert max-w-none"
                            style={{ 
                                whiteSpace: 'pre-wrap',
                                lineHeight: '2',
                                fontSize: '1.125rem',
                                color: '#e5e7eb'
                            }}
                        >
                            {story.content}
                        </div>
                    </div>
                    
                    {/* Závěrečná dekorace */}
                    <div className="text-center py-8">
                        <div className="inline-flex items-center space-x-3 text-yellow-500/30">
                            <span className="text-2xl">🌿</span>
                            <span className="text-2xl">·</span>
                            <span className="text-2xl">🌸</span>
                            <span className="text-2xl">·</span>
                            <span className="text-2xl">🌿</span>
                        </div>
                    </div>
                    
                    {/* Citace nebo poznámka */}
                    {story.note && (
                        <div className="mt-12 border-l-4 border-yellow-500/50 pl-6 py-4 italic text-gray-400">
                            {story.note}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SingleStoryPage;
